import React from 'react';
import { motion } from 'framer-motion';
import { Anchor, VolumeX, HeartHandshake } from 'lucide-react';
export function MechanismReveal() {
  const steps = [{
    icon: <Anchor className="w-7 h-7 text-sage" data-id="element-138" />,
    title: 'Stage 1: The Caregiver Anchor',
    description: "You regulate yourself first. Your nervous system is the co-regulator. If you're dysregulated, his escalates. This takes 90 seconds."
  }, {
    icon: <VolumeX className="w-7 h-7 text-sage" data-id="element-139" />,
    title: 'Stage 2: Sensory Load Reduction',
    description: 'You identify and remove the top sensory trigger in the environment. Not all of them. Just the loudest one. This interrupts the escalation cycle before it peaks.'
  }, {
    icon: <HeartHandshake className="w-7 h-7 text-sage" data-id="element-140" />,
    title: 'Stage 3: Silent Co-Regulation',
    description: 'You re-enter his space using presence, not words. No instructions. No questions. No explanations. Just proximity and calm. His nervous system mirrors yours.'
  }];
  return <section className="w-full py-20 px-6 md:px-12 lg:px-24 bg-cream-dark/50" data-id="element-141">
      <div className="max-w-4xl mx-auto" data-id="element-142">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.8
      }} className="space-y-6 text-base md:text-lg text-charcoal-light/80 leading-relaxed mb-12" data-id="element-143">
          <h2 className="text-2xl md:text-3xl font-serif text-charcoal/70 mb-8 text-center" data-id="element-144">
            The Mechanism Reveal
          </h2>

          <p data-id="element-145">
            Here is what we know about the autistic nervous system that changes
            everything:
          </p>
          <p data-id="element-146">
            Your child is not choosing to melt down. His brain is reaching a
            state called{' '}
            <span className="font-semibold text-charcoal/70" data-id="element-147">
              neurological overwhelm
            </span>{' '}
            — a point where the sensory, emotional and cognitive load exceeds
            his current capacity to regulate.
          </p>
          <p data-id="element-148">This state does not appear without warning.</p>
          <p data-id="element-149">
            There is always a{' '}
            <span className="font-semibold text-sage" data-id="element-150">yellow zone</span>. A
            window of ten, fifteen, sometimes thirty minutes where the signals
            are present, the load is building and intervention is not only
            possible — it is highly effective.
          </p>
          <p data-id="element-151">
            The problem is that most parents have never been taught to read that
            window.
          </p>
          <p className="text-xl font-serif text-charcoal/70 text-center pt-6" data-id="element-152">
            The Crisis Calm Method is built around three stages:
          </p>
        </motion.div>

        <div className="grid gap-6 md:grid-cols-3" data-id="element-153">
          {steps.map((step, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.2
        }} className="bg-white/60 p-6 rounded-xl flex flex-col items-center text-center border border-cream-dark" data-id="element-154">
              <div className="bg-sage-light p-3 rounded-full mb-4" data-id="element-155">
                {step.icon}
              </div>
              <h3 className="text-lg font-serif text-charcoal/70 font-semibold mb-3" data-id="element-156">
                {step.title}
              </h3>
              <p className="text-charcoal-light/70 text-sm leading-relaxed" data-id="element-157">
                {step.description}
              </p>
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8,
        delay: 0.8
      }} className="mt-12 text-center space-y-4" data-id="element-158">
          <p className="text-xl font-serif text-charcoal/60" data-id="element-159">
            Three steps. No yelling. No punishment. No shame.
          </p>
          <p className="text-base text-charcoal-light/60" data-id="element-160">
            And once you understand why each step works at a neurological level,
            you cannot unsee it.
          </p>

          <div className="pt-8" data-id="element-161">
            <a href="#final-cta" className="inline-block bg-amber hover:bg-amber-hover text-white font-semibold text-lg px-8 py-4 rounded-full transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5" data-id="element-162">
              Yes, I Want to Stop the Next Meltdown Before It Starts
            </a>
          </div>
        </motion.div>
      </div>
    </section>;
}